import React, { useEffect, useState } from 'react'

import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import LinkRouter from '@e3dc-react/shell/src/Elements/Links/LinkRouter'
import { makeStyles } from 'tss-react/mui'
import { CategoryItemType } from './types'
import type { Category, CategoryItem } from './types'
import { J1DownarrowD, J1RightarrowD, J1ToparrowD } from '@e3dc-react/icons'
import { Box, Collapse, List, ListItemButton, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { NewLabel } from '../../Libs/new-content'
import { isRouteActive } from 'src/Libs/navigation/utils/is-route-active'
import { SIDEBAR_LINK_PREFIX, SIDEBAR_SUBMENU_TOGGLE_SUFFIX } from 'src/Libs/qa/ids'

const useStyles = makeStyles()(theme => ({
  item: {
    paddingLeft: theme.spacing(4),
    textDecoration: 'none',
    color: theme.palette.text.sidebar,
    position: 'relative',
    height: '36px',

    '&:hover,&:focus': {
      textDecoration: 'none',
    },
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
      minHeight: '36px',
      height: 'auto',
    },
  },
  itemDisabled: {
    color: theme.palette.grey.grey04,
    paddingLeft: theme.spacing(4),
    cursor: 'not-allowed',
    height: '36px',

    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
      minHeight: '36px',
      height: 'auto',
    },
  },

  itemActiveItem: {
    '& *': {
      color: theme.palette.sidebar.active,
      fill: theme.palette.sidebar.active,
      fontWeight: 'bold',
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.sidebar.active,
    },

    '& .MuiListItemText-root .MuiListItemText-primary': {
      color: theme.palette.sidebar.active,
      fontWeight: 500,
    },
  },
  itemActiveWithIndicator: {
    paddingLeft: 16,
    '& .MuiListItemIcon-root': {
      paddingLeft: 8,
      borderLeft: `2px solid ${theme.palette.primary.light}`,
    },
  },
  label: {
    display: 'flex',
    gap: theme.spacing(1),
    fontSize: 'inherit',
    textDecoration: 'none',
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  itemPrimary: {
    fontSize: 'inherit',
    textDecoration: 'none',
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  itemIcon: {
    minWidth: 'auto',
    marginRight: theme.spacing(1),
    marginLeft: -3,
    color: theme.palette.text.sidebar,

    '& svg': {
      fill: theme.palette.text.sidebar,
      fontSize: 24,
    },
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.text.sidebar,

    '&:hover,&:focus': {
      textDecoration: 'none',
    },
  },
  linkDisabled: {
    textDecoration: 'none',
    '&:hover,&:focus': {
      textDecoration: 'none',
    },
  },
  hide: {
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  listItemButton: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: 0,
    '& *': {
      color: theme.palette.text.sidebar,
    },
  },
  listItemButtonActive: {
    '& *': {
      color: theme.palette.sidebar.active,
    },
  },
  listItemButtonIcon: {
    flexGrow: 0,
  },
  selectContent: {
    borderLeftWidth: 1,
    borderLeftStyle: 'solid',
    borderLeftColor: theme.palette.grey.grey04,
    marginLeft: theme.spacing(8),
  },
  selectSubItem: {
    padding: 0,
    height: '30px',
    [theme.breakpoints.down('md')]: {
      minHeight: '30px',
      height: 'auto',
    },
  },
  rightIcon: {
    display: 'flex',
    justifyContent: 'flex-end',
    color: theme.palette.text.sidebar,
    '& svg': {
      fontSize: 24,
    },
  },
}))

export interface CategoryItemListProps {
  categoryItems: CategoryItem[]
  onClose?: () => void
  handleCategoryListItemClick?: (item: CategoryItem, items?: Category[]) => void
  collapsed?: boolean
  categoryItemClasses?: string
  showActiveIndicator?: boolean
}

export interface CategoryListItemProps {
  disabled?: boolean
  icon?: React.ReactNode
  hasNewContent?: boolean
  translationKey: string
  onClick?: () => void
  active?: boolean
  dataQa: string
  collapsed?: boolean
  selectItems?: CategoryItem[]
  className?: string
  href?: string
  categoryItemClasses?: string
  showActiveIndicator?: boolean
  rightIcon?: React.ReactElement
}

/**
 * Display Icon and label for a category item in the sidebar navigation
 */
export const CategoryListItem: React.FC<CategoryListItemProps> = ({
  disabled,
  icon,
  hasNewContent = false,
  translationKey,
  onClick,
  active,
  dataQa,
  collapsed,
  selectItems,
  className,
  href,
  categoryItemClasses,
  showActiveIndicator,
  rightIcon,
}) => {
  const { classes, cx } = useStyles()
  const navigate = useNavigate()
  const [submenuOpen, setSubmenuOpen] = useState(false)

  return (
    <>
      <ListItem
        button
        disabled={disabled}
        className={cx(
          {
            [classes.item]: !disabled,
            [classes.itemActiveItem]: active,
            [classes.itemActiveWithIndicator]: active && showActiveIndicator,
            [classes.itemDisabled]: disabled,
          },
          className,
          categoryItemClasses
        )}
        onClick={() => {
          if (!selectItems) {
            onClick?.()
          }
        }}
        id={dataQa}
        data-qa={`${SIDEBAR_LINK_PREFIX}${dataQa}`}
        aria-current={active ? 'page' : undefined}
      >
        <ListItemButton
          className={cx(classes.listItemButton, active && classes.listItemButtonActive)}
          onClick={() => {
            if (selectItems) {
              navigate(href!)
              onClick?.()
            }
          }}
        >
          <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
          {!collapsed && (
            <ListItemText className={classes.label}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {translationKey}
                </Typography>
                {hasNewContent && <NewLabel />}
              </Box>
            </ListItemText>
          )}
          {rightIcon && <ListItemIcon className={classes.rightIcon}>{rightIcon}</ListItemIcon>}
        </ListItemButton>
        {selectItems && (
          <ListItemButton
            className={cx(classes.listItemButton, classes.listItemButtonIcon)}
            onClick={() => setSubmenuOpen(!submenuOpen)}
            data-qa={`${SIDEBAR_LINK_PREFIX}${dataQa}${SIDEBAR_SUBMENU_TOGGLE_SUFFIX}`}
          >
            {submenuOpen && !collapsed ? <J1ToparrowD /> : <J1DownarrowD />}
          </ListItemButton>
        )}
      </ListItem>

      {selectItems && (
        <CategoryItemSelect
          submenuOpen={submenuOpen}
          items={selectItems}
          collapsed={collapsed}
          categoryItemClasses={categoryItemClasses}
          onSubMenuIsOpen={open => setSubmenuOpen(open)}
          onClick={onClick}
        />
      )}
    </>
  )
}

const CategoryItemSelect: React.FC<{
  submenuOpen: boolean
  items: CategoryItem[]
  collapsed?: boolean
  categoryItemClasses?: string
  onSubMenuIsOpen: (open: boolean) => void
  onClick?: (item: CategoryItem) => void
}> = ({ submenuOpen, items, collapsed, categoryItemClasses, onSubMenuIsOpen, onClick }) => {
  const { classes, cx } = useStyles()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(submenuOpen)

  useEffect(() => {
    setIsOpen(submenuOpen)
    onSubMenuIsOpen(submenuOpen)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submenuOpen])

  const handleOnClick = (item: CategoryItem): void => {
    onClick?.(item)
  }

  return (
    <Collapse in={isOpen && !collapsed} timeout="auto" unmountOnExit>
      <List component="div" disablePadding className={classes.selectContent}>
        {items.map(selectItem => {
          const { translationKey, icon, hasNewContent, href, disabled, dataQa, hide } = selectItem
          const markActive = isRouteActive(location.pathname, href)
          if (hide) return null
          return (
            <LinkRouter
              to={href ?? '/'}
              key={translationKey}
              className={cx(disabled ? classes.linkDisabled : classes.link, categoryItemClasses)}
              disabled={disabled}
            >
              <Box sx={theme => ({ paddingLeft: theme.spacing(1) })}>
                <CategoryListItem
                  disabled={disabled}
                  icon={icon}
                  hasNewContent={hasNewContent}
                  onClick={() => handleOnClick(selectItem)}
                  translationKey={translationKey}
                  active={markActive}
                  dataQa={dataQa}
                  collapsed={collapsed}
                  className={classes.selectSubItem}
                  categoryItemClasses={cx(disabled ? classes.linkDisabled : classes.link, categoryItemClasses)}
                />
              </Box>
            </LinkRouter>
          )
        })}
      </List>
    </Collapse>
  )
}

const CategoryItemList: React.FC<CategoryItemListProps> = ({
  categoryItems,
  onClose,
  handleCategoryListItemClick,
  collapsed,
  categoryItemClasses,
  showActiveIndicator,
}) => {
  const { classes, cx } = useStyles()
  const location = useLocation()

  const handleOnClick = (item?: CategoryItem, items?: Category[]): void => {
    if (item) handleCategoryListItemClick?.(item, items)
    if (onClose) onClose()
  }

  if (!categoryItems) return null

  return (
    <>
      {categoryItems.map(item => {
        const { translationKey, icon, hasNewContent, href, activeHref, disabled, items, selectItems, exact, hide, rightIcon } = item
        const markActive = isRouteActive(location.pathname, activeHref ?? href, Boolean(exact))

        const elementKey = `list-item-${translationKey}`

        if (hide) return null

        if (markActive && items) {
          handleCategoryListItemClick?.(item)
        }

        if (items) {
          return (
            <ListItem
              button
              key={elementKey}
              disabled={disabled}
              className={cx(
                { [classes.item]: !disabled, [classes.itemActiveItem]: markActive, [classes.itemDisabled]: disabled },
                categoryItemClasses
              )}
              onClick={() => {
                handleOnClick(item, items)
              }}
              data-qa={`sidebar-link-${item.dataQa}`}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} className={cx(classes.itemPrimary, { [classes.hide]: collapsed })}>
                {translationKey}
              </ListItemText>
              <J1RightarrowD />
            </ListItem>
          )
        }

        if (selectItems) {
          return (
            <CategoryListItem
              key={elementKey}
              disabled={disabled}
              onClick={handleOnClick}
              icon={icon}
              hasNewContent={hasNewContent}
              translationKey={translationKey}
              active={markActive}
              dataQa={item.dataQa}
              collapsed={collapsed}
              selectItems={selectItems}
              categoryItemClasses={categoryItemClasses}
              href={href ?? '/'}
              showActiveIndicator={showActiveIndicator}
            />
          )
        }

        if (item.type === CategoryItemType.BUTTON && item.onClick) {
          return (
            <CategoryListItem
              key={translationKey}
              disabled={disabled}
              onClick={() => item.onClick?.()}
              icon={icon}
              hasNewContent={hasNewContent}
              translationKey={translationKey}
              categoryItemClasses={categoryItemClasses}
              active={markActive}
              dataQa={item.dataQa}
              collapsed={collapsed}
              showActiveIndicator={showActiveIndicator}
            />
          )
        }

        return (
          <LinkRouter
            to={href ?? '/'}
            key={translationKey}
            className={cx(disabled ? classes.linkDisabled : classes.link, categoryItemClasses)}
            disabled={disabled}
          >
            <CategoryListItem
              disabled={disabled}
              onClick={() => {
                handleOnClick(item, items)
              }}
              icon={icon}
              hasNewContent={hasNewContent}
              translationKey={translationKey}
              categoryItemClasses={categoryItemClasses}
              active={markActive}
              dataQa={item.dataQa}
              collapsed={collapsed}
              showActiveIndicator={showActiveIndicator}
              rightIcon={rightIcon}
            />
          </LinkRouter>
        )
      })}
    </>
  )
}

export default CategoryItemList
